import { supabase } from '../supabase';
import { newRequestId } from './weightService';

/**
 * Body scans (migration 081). The database decides who may read, add and correct (the client, their
 * own coach, the super admin; staff correct only what they entered), stamps who and when, keeps a
 * scan's weight in the one weight record, and keeps a deleted scan 30 days. Every write here asks for
 * the row back: no row means refused or out of date, never "saved".
 */
const COLUMNS = 'id, client_id, measured_on, machine, weight_kg, body_fat_pct, muscle_kg, muscle_kind, entry_method, weight_log_id, version, created_by, created_at, updated_at, deleted_at';

export { newRequestId };

export async function listScans(clientId) {
  const { data, error } = await supabase
    .from('body_scans')
    .select(COLUMNS)
    .eq('client_id', clientId)
    .is('deleted_at', null)
    .order('measured_on', { ascending: false })
    .order('id', { ascending: false })
    .limit(200);
  if (error) throw error;
  return data || [];
}

const toRow = ({ measuredOn, machine, weight, fat, muscle, muscleKind, entryMethod }) => {
  const n = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  const muscleKg = n(muscle);
  return {
    measured_on: measuredOn,
    machine: machine.trim(),
    weight_kg: n(weight),
    body_fat_pct: n(fat),
    muscle_kg: muscleKg,
    muscle_kind: muscleKg === null ? null : muscleKind,
    ...(entryMethod ? { entry_method: entryMethod } : {}),
  };
};

const fail = (label, error) => {
  const err = new Error(`${label}: ${error?.message || 'refused'}`);
  err.cause = error;
  return err;
};

/** Adds a scan. Give the same requestId when retrying the same scan: it is then never stored twice. */
export async function addScan(clientId, fields, requestId = newRequestId()) {
  const { data, error } = await supabase
    .from('body_scans')
    .insert({ client_id: clientId, request_id: requestId, ...toRow(fields) })
    .select(COLUMNS)
    .single();
  if (!error) return data;
  if (error.code === '23505') {
    // The same scan again (a retry after a lost answer): it is already stored
    const { data: existing } = await supabase.from('body_scans').select(COLUMNS)
      .eq('client_id', clientId).eq('request_id', requestId).maybeSingle();
    if (existing) return existing;
  }
  throw fail('Saving the scan', error);
}

const updateAt = async (label, scan, changes) => {
  const { data, error } = await supabase
    .from('body_scans')
    .update(changes)
    .eq('id', scan.id)
    .eq('version', scan.version)
    .select(COLUMNS);
  if (error) throw fail(label, error);
  if (!data || data.length === 0) {
    throw fail(label, { message: 'it was changed elsewhere, or you may not change it. Close it and open it again.' });
  }
  return data[0];
};

export const correctScan = (scan, fields) => updateAt('Saving the correction', scan, toRow(fields));
export const deleteScan = (scan) => updateAt('Deleting the scan', scan, { deleted_at: new Date().toISOString() });

/** Undo a delete (the database allows it for 30 days). Reads the deleted row by id to get its version. */
export async function restoreScan(scanId) {
  const { data: row, error } = await supabase.from('body_scans').select(COLUMNS).eq('id', scanId).maybeSingle();
  if (error || !row) throw fail('Restoring the scan', error);
  return updateAt('Restoring the scan', row, { deleted_at: null });
}

/** The day of the client's latest scan (YYYY-MM-DD), or null if they have none: for the four-weekly reminder. */
/** When the client joined (their account's created_at), or null: no first-scan reminder in week one. */
export async function clientJoinedAt(clientId) {
  const { data, error } = await supabase.from('users').select('created_at').eq('auth_user_id', clientId).maybeSingle();
  if (error) throw error;
  return data?.created_at ?? null;
}

/** The client's latest scan ({ measured_on, machine }), or null: for the four-weekly reminder. */
export async function latestScan(clientId) {
  const { data, error } = await supabase.from('body_scans').select('measured_on, machine')
    .eq('client_id', clientId).is('deleted_at', null)
    .order('measured_on', { ascending: false }).limit(1).maybeSingle();
  if (error) throw error;
  return data ?? null;
}

// ── Reading a photo of the sheet (plan 008, step 5) ─────────────────────────
// The photo is shrunk on the phone (longest side 1600 px, a JPEG under 2 MB) with the browser's own
// canvas, then sent to the read-body-scan function, which reads it with OpenAI and keeps neither the
// photo nor the answer (only the daily count: who asked, for whom, when; migration 082).

const MAX_SIDE = 1600;
const MAX_BYTES = 2 * 1024 * 1024;
const MAX_SOURCE_BYTES = 30 * 1024 * 1024; // larger than any phone photo; not decoded at all above this

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file is not a photo we can open. Try a JPEG or a photo from your camera.')); };
  img.src = url;
});

/** The photo as a small JPEG, base64 (no "data:" prefix), and a preview address for the screen. */
export async function shrinkPhoto(file) {
  if (file.size > MAX_SOURCE_BYTES) throw new Error('That file is too large to be a photo of the sheet. Take a new photo with your camera.');
  const img = await loadImage(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  for (const quality of [0.85, 0.7, 0.55]) {
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    if (base64.length * 0.75 <= MAX_BYTES) return { base64, preview: dataUrl };
  }
  throw new Error('That photo is too large. Try again a little further from the sheet.');
}

const READ_ERRORS = {
  limit: "The daily limit of 10 photo readings has been reached. Type the numbers instead, or try again later.",
  not_a_sheet: "We couldn't find a body composition sheet in that photo.",
  could_not_read: "We couldn't read that photo.",
  not_a_photo: 'That file is not a photo we can read.',
  photo_too_large: 'That photo is too large.',
  not_allowed: 'You cannot add a scan for this person.',
  repeat: 'That photo was already read.',
};

/** Sends the shrunk photo to be read. Returns { reading } or throws an Error with a plain message and .code. */
export async function readScanPhoto(clientId, base64, requestId = newRequestId()) {
  const { data, error } = await supabase.functions.invoke('read-body-scan', {
    body: { client_id: clientId, request_id: requestId, image_base64: base64 },
  });
  if (!error && data?.reading) return data;
  let reply = data;
  try { if (!reply?.error && error?.context?.json) reply = await error.context.json(); } catch { /* not JSON */ }
  const code = reply?.error;
  let message = READ_ERRORS[code] || "We couldn't read that photo. Check your connection and try again, or type the numbers.";
  // The limit is shared by the client and whoever reads for them (10 a day each), and resets at midnight UTC
  const resets = code === 'limit' && reply?.resets_at ? new Date(reply.resets_at) : null;
  if (resets && !Number.isNaN(resets.getTime())) {
    message = `The daily limit of 10 photo readings has been reached. Type the numbers instead, or try again after ${resets.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.`;
  }
  const err = new Error(message);
  err.code = code || 'failed';
  throw err;
}
